import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { toApiError, useApi, type CreateResourceBlockRequest } from '@/shared/api';
import { occupancyFailureText } from '../resourceProblemText';

const occupancyKey = (groupId: string) => ['property-groups', groupId, 'resources', 'occupancy'];

export function useResourceOccupancy(groupId: string | undefined, from: string, to: string) {
  const api = useApi();
  return useQuery({
    queryKey: [...occupancyKey(groupId ?? ''), from, to],
    queryFn: () => api.resources.occupancy(groupId!, from, to),
    enabled: Boolean(groupId) && Boolean(from) && Boolean(to),
  });
}

/** Sperren und Aufheben. Eine Ablehnung bleibt als Satz stehen; keine Antwort ist kein Nein. */
export function useResourceMutations(groupId: string, onBlocked?: () => void) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: occupancyKey(groupId) });
  const fail = (action: 'block' | 'cancel') => async (failure: unknown) => {
    setError(occupancyFailureText(await toApiError(failure), action));
    void refresh();
  };

  const block = useMutation({
    mutationFn: (body: CreateResourceBlockRequest) => api.resources.createBlock(groupId, body),
    networkMode: 'always',
    onMutate: () => setError(null),
    onSuccess: async () => {
      await refresh();
      toast.success('Zeit gesperrt');
      onBlocked?.();
    },
    onError: fail('block'),
  });

  const cancel = useMutation({
    mutationFn: ({ id, confirmPaid }: { id: string; confirmPaid: boolean }) =>
      api.resources.cancelReservation(groupId, id, confirmPaid),
    networkMode: 'always',
    onMutate: () => setError(null),
    onSuccess: async () => {
      await refresh();
      toast.success('Aufgehoben');
    },
    onError: fail('cancel'),
  });

  return { block, cancel, error, clearError: () => setError(null) };
}
